/** Loan interest and payoff projection (loans.md §3–4). Pure; amounts are native minor units, balances positive. */
import { daysBetween, type IsoDate } from './dates';
import { divRound } from './money';
import { addMonthsClamped } from './schedule';

/** Annual rates are percent × 10 000: 6,25 % = 62 500. */
export const LOAN_RATE_SCALE = 10_000;
export const INTEREST_METHODS = ['30_360', 'actual_365'] as const;
export type InterestMethod = (typeof INTEREST_METHODS)[number];

export interface LoanRate {
  effectiveFrom: IsoDate;
  annualRate: number;
}

/** The rate effective on `date`: the latest change on or before it, else the earliest known. */
export function rateOn(rates: LoanRate[], date: IsoDate): number {
  if (!rates.length) return 0;
  const sorted = [...rates].sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? -1 : 1));
  let r = sorted[0]!.annualRate;
  for (const x of sorted) if (x.effectiveFrom <= date) r = x.annualRate;
  return r;
}

/** Interest for one period on a positive balance. 30/360 ignores `days`. */
export function periodInterest(
  balance: number,
  annualRate: number,
  method: InterestMethod,
  days = 30,
): number {
  if (balance <= 0 || annualRate <= 0) return 0;
  const denom = 100n * BigInt(LOAN_RATE_SCALE) * (method === '30_360' ? 12n : 365n);
  const num = BigInt(balance) * BigInt(annualRate) * (method === '30_360' ? 1n : BigInt(Math.max(0, days)));
  return Number(divRound(num, denom));
}

export interface AmortizeInput {
  /** Remaining debt now (positive). */
  balance: number;
  rates: LoanRate[];
  installment: number;
  method: InterestMethod;
  /** Date of the next payment. */
  firstPayment: IsoDate;
  /** Date interest last accrued (actual/365 only); defaults to one month before the first payment. */
  lastAccrual?: IsoDate;
  extraMonthly?: number;
  oneOff?: { date: IsoDate; amount: number } | null;
  maxMonths?: number;
}

export interface AmortizeRow {
  date: IsoDate;
  interest: number;
  principal: number;
  payment: number;
  balance: number;
}

export interface Amortization {
  rows: AmortizeRow[];
  /** Date of the last payment, or null when the installment never pays the loan off. */
  payoffDate: IsoDate | null;
  totalInterest: number;
}

/** Month-by-month schedule until the balance reaches zero. */
export function amortize(input: AmortizeInput): Amortization {
  const max = input.maxMonths ?? 600;
  const extra = Math.max(0, input.extraMonthly ?? 0);
  let balance = input.balance;
  let prev = input.lastAccrual ?? addMonthsClamped(input.firstPayment, -1);
  let oneOffLeft = input.oneOff && input.oneOff.amount > 0 ? input.oneOff : null;
  const rows: AmortizeRow[] = [];
  let totalInterest = 0;
  for (let n = 0; n < max && balance > 0; n++) {
    const date = addMonthsClamped(input.firstPayment, n);
    if (oneOffLeft && oneOffLeft.date <= date) {
      balance = Math.max(0, balance - oneOffLeft.amount);
      oneOffLeft = null;
      if (balance === 0) {
        rows.push({ date: input.oneOff!.date, interest: 0, principal: 0, payment: 0, balance: 0 });
        break;
      }
    }
    const interest = periodInterest(
      balance,
      rateOn(input.rates, date),
      input.method,
      daysBetween(prev, date),
    );
    const due = balance + interest;
    const payment = Math.min(input.installment + extra, due);
    if (payment <= interest && payment < due) return { rows, payoffDate: null, totalInterest };
    balance = due - payment;
    totalInterest += interest;
    rows.push({ date, interest, principal: payment - interest, payment, balance });
    prev = date;
  }
  return { rows, payoffDate: balance <= 0 ? (rows[rows.length - 1]?.date ?? null) : null, totalInterest };
}

export interface WhatIf {
  payoffDate: IsoDate | null;
  totalInterest: number;
  monthsSaved: number;
  interestSaved: number;
}

/** Effect of paying extra each month and/or once, compared with the plain schedule. */
export function whatIf(
  input: AmortizeInput,
  extraMonthly: number,
  oneOff?: { date: IsoDate; amount: number } | null,
): WhatIf {
  const base = amortize({ ...input, extraMonthly: 0, oneOff: null });
  const alt = amortize({ ...input, extraMonthly, oneOff: oneOff ?? null });
  return {
    payoffDate: alt.payoffDate,
    totalInterest: alt.totalInterest,
    monthsSaved: base.payoffDate && alt.payoffDate ? base.rows.length - alt.rows.length : 0,
    interestSaved: base.payoffDate && alt.payoffDate ? base.totalInterest - alt.totalInterest : 0,
  };
}

/** `paymentDay` in the month of `date`, clamped to the month's length (31 → 30 in April). */
function onDay(date: IsoDate, paymentDay: number): IsoDate {
  const [y, m] = date.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${date.slice(0, 7)}-${String(Math.min(paymentDay, last)).padStart(2, '0')}`;
}

/** The first payment day strictly after the loan was opened: opened 18.03, paid on the 18th → 18.04. */
export function firstPaymentAfter(startDate: IsoDate, paymentDay: number): IsoDate {
  const same = onDay(startDate, paymentDay);
  return same > startDate ? same : onDay(addMonthsClamped(`${startDate.slice(0, 7)}-01`, 1), paymentDay);
}

export interface LoanContract {
  /** The amount borrowed (positive). */
  originalPrincipal: number;
  /** Date the money was paid out. */
  startDate: IsoDate;
  paymentDay: number;
  installment: number;
  rates: LoanRate[];
  method: InterestMethod;
}

/**
 * The bank's repayment plan (plan otplate) from the contract: every payment from the first one, starting from
 * the full amount borrowed. Interest for the first period accrues from the payout date.
 */
export function contractSchedule(c: LoanContract): Amortization {
  return amortize({
    balance: c.originalPrincipal,
    rates: c.rates,
    installment: c.installment,
    method: c.method,
    firstPayment: firstPaymentAfter(c.startDate, c.paymentDay),
    lastAccrual: c.startDate,
  });
}

/** What the contract says is owed right after the last payment on or before `date` (the full amount before the first). */
export function contractBalanceOn(
  c: LoanContract,
  date: IsoDate,
): { balance: number; after: IsoDate | null } {
  let balance = c.originalPrincipal;
  let after: IsoDate | null = null;
  for (const r of contractSchedule(c).rows) {
    if (r.date > date) break;
    balance = r.balance;
    after = r.date;
  }
  return { balance, after };
}
